import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Button } from '@/components/ui/button.jsx'
import ThemeToggle from '@/components/ThemeToggle.jsx'
import useAsset from '@/hooks/useAsset'
import LandingPage from '@/components/LandingPage.jsx'
import InvestorPage from '@/components/InvestorPage.jsx'
import OrderPage from '@/components/OrderPage.jsx'
import CateringPage from '@/components/CateringPage.jsx'
import Footer from '@/components/Footer.jsx'
import InvestorForm from '@/components/InvestorForm.jsx'
import ContactForm from '@/components/ContactForm.jsx'
import DesignSystem from '@/components/DesignSystemSimple.jsx'
import PrivacyPolicy from '@/components/PrivacyPolicy.jsx'
import TermsOfUse from '@/components/TermsOfUse.jsx'
import TermsOfSale from '@/components/TermsOfSale.jsx'
import TermsOfDonation from '@/components/TermsOfDonation.jsx'
import SecretApp from './secret/SecretApp.tsx'
import './App.css'
import './secret/secret-index.css'

// Import Secret App Components
import SecretLandingPage from './secret/pages/LandingPage.tsx'
import SecretLoginPage from './secret/pages/auth/LoginPage.tsx'
import SecretRegisterPage from './secret/pages/auth/RegisterPage.tsx'
import SecretForgotPasswordPage from './secret/pages/auth/ForgotPasswordPage.tsx'
import SecretDashboardPage from './secret/pages/DashboardPage.tsx'
import { ThemeProvider } from 'next-themes'

// Import necessary context providers
import { AuthProvider } from './secret/contexts/AuthContext.tsx'
import { PaymentProvider } from './secret/contexts/PaymentContext.tsx'
import { CartProvider } from './secret/contexts/CartContext.tsx'
import { NotificationProvider } from './secret/contexts/NotificationContext.tsx'

// Import Donate Page and Context
import DonatePage from './donate/pages/DonatePage.tsx'
import { DonationProvider } from './donate/contexts/DonationContext.tsx'
import { DonationPaymentProvider } from './donate/contexts/DonationPaymentContext.tsx'

// Import Taste Page and Context
import TastePage from './taste/pages/TastePage.tsx'
import { TasteProvider } from './taste/contexts/TasteContext.tsx'
import { TastePaymentProvider } from './taste/contexts/TastePaymentContext.tsx'

// Import Wholesale Page and Context
import WholesalePage from './wholesale/pages/WholesalePage.tsx'
import { WholesaleProvider } from './wholesale/contexts/WholesaleContext.tsx'

// Import Labels (internal dry-ice label generator)
import LabelsPage from './labels/LabelsPage.tsx'

// Wrapper component for secret pages with all necessary providers
const SecretPageWrapper = ({ children }) => {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} disableTransitionOnChange={true} storageKey="tailtations-theme">
      <AuthProvider>
        <PaymentProvider>
          <CartProvider>
            <NotificationProvider>
              {children}
            </NotificationProvider>
          </CartProvider>
        </PaymentProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

function App() {
  const [showInvestorForm, setShowInvestorForm] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [isSecretSubdomain, setIsSecretSubdomain] = useState(false)
  const { theme, resolvedTheme } = useTheme()
  const location = useLocation()

  // Cache-busted asset URLs
  const logoWhite = useAsset('/tailtations-white.svg')
  const logoDark = useAsset('/tailtations.svg')
  
  

  // Check if we're on the SECRET subdomain or path
  useEffect(() => {
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    
    // Check for subdomain (production) or localhost subdomain
    const isSecretSubdomain = hostname.startsWith('secret.') || hostname === 'secret.localhost'
    
    // Check for secret path (fallback)
    const isSecretPath = pathname.startsWith('/secret')
    
    // Enable SECRET app only for subdomain or secret path, but exclude design-system
    const isSecret = (isSecretSubdomain || isSecretPath) && !pathname.startsWith('/design-system')
    
    setIsSecretSubdomain(isSecret)
  }, [location.pathname])

  // If we're on the SECRET subdomain, render the SECRET app
  if (isSecretSubdomain) {
    return <SecretApp />
  }

  // Use Routes for proper routing
  return (
    <Routes>
      {/* Home Page */}
      <Route path="/" element={
        <div className="min-h-screen">
          <LandingPage />
          <Footer />
          
          {/* Modal Forms */}
          <InvestorForm 
            isOpen={showInvestorForm} 
            onClose={() => setShowInvestorForm(false)} 
          />
          <ContactForm 
            isOpen={showContactForm} 
            onClose={() => setShowContactForm(false)} 
          />
        </div>
      } />

      {/* Investor/Order Pages with Header Layout */}
      <Route path="/investors" element={
        <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 investor-page">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-md border-b border-primary/20 sticky top-0 z-50">
            <div className="container mx-auto px-4 py-8 flex justify-between items-center">
              <div className="flex items-center">
                <Link to="/">
                  <img
                    src={resolvedTheme === 'dark' ? logoWhite : logoDark}
                    alt="Tailtations"
                    className="h-15"
                    style={{ height: '84px' }}
                  />
                </Link>
              </div>
              <nav className="hidden md:flex space-x-6">
                <a href="#overview" onClick={(e) => { e.preventDefault(); const el = document.getElementById('overview'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-black/60 hover:text-primary transition-colors cursor-pointer">Business Model</a>
                <a href="#opportunity" onClick={(e) => { e.preventDefault(); const el = document.getElementById('opportunity'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-black/60 hover:text-primary transition-colors cursor-pointer">Opportunity</a>
                <a href="#financials" onClick={(e) => { e.preventDefault(); const el = document.getElementById('financials'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Financials</a>
                <a href="#team" onClick={(e) => { e.preventDefault(); const el = document.getElementById('team'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Team</a>
                <a href="#contact" onClick={(e) => { e.preventDefault(); const el = document.getElementById('contact'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Contact</a>
              </nav>
              <div className="flex items-center gap-3">
                <Button 
                  className="bg-primary text-white hover:bg-primary/90 hover:text-white"
                  onClick={() => setShowInvestorForm(true)}
                >
                  Invest Now
                </Button>
              </div>
            </div>
          </header>

          <InvestorPage />
          <Footer />

          {/* Modal Forms */}
          <InvestorForm 
            isOpen={showInvestorForm} 
            onClose={() => setShowInvestorForm(false)} 
          />
          <ContactForm 
            isOpen={showContactForm} 
            onClose={() => setShowContactForm(false)} 
          />
        </div>
      } />

      <Route path="/order" element={
        <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-md border-b border-primary/20 sticky top-0 z-50">
            <div className="container mx-auto px-4 py-8 flex justify-between items-center">
              <div className="flex items-center">
                <Link to="/">
                  <img
                    src={resolvedTheme === 'dark' ? logoWhite : logoDark}
                    alt="Tailtations"
                    className="h-15"
                    style={{ height: '60px' }}
                  />
                </Link>
              </div>
              <nav className="hidden md:flex space-x-6">
                <a href="#overview" onClick={(e) => { e.preventDefault(); const el = document.getElementById('overview'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-black/60 hover:text-primary transition-colors cursor-pointer">Business Model</a>
                <a href="#opportunity" onClick={(e) => { e.preventDefault(); const el = document.getElementById('opportunity'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-black/60 hover:text-primary transition-colors cursor-pointer">Opportunity</a>
                <a href="#financials" onClick={(e) => { e.preventDefault(); const el = document.getElementById('financials'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Financials</a>
                <a href="#team" onClick={(e) => { e.preventDefault(); const el = document.getElementById('team'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Team</a>
                <a href="#contact" onClick={(e) => { e.preventDefault(); const el = document.getElementById('contact'); if (el) { const y = el.getBoundingClientRect().top + window.pageYOffset - (275 - 125); window.scrollTo({ top: y, behavior: 'smooth' }); } }} className="text-gray-600 hover:text-primary transition-colors cursor-pointer">Contact</a>
              </nav>
              <div className="flex items-center gap-3">
                <div className="ml-4">
                  <ThemeToggle />
                </div>
              </div>
            </div>
          </header>

          <OrderPage />
          <Footer />

          {/* Modal Forms */}
          <InvestorForm
            isOpen={showInvestorForm}
            onClose={() => setShowInvestorForm(false)}
          />
          <ContactForm
            isOpen={showContactForm}
            onClose={() => setShowContactForm(false)}
          />
        </div>
      } />

      {/* Catering Page */}
      <Route path="/catering" element={
        <div className="min-h-screen">
          <CateringPage />
          <Footer />
        </div>
      } />

      {/* Donate Page - Feed Skid Row This Thanksgiving */}
      <Route path="/donate" element={
        <DonationProvider>
          <DonationPaymentProvider>
            <DonatePage />
          </DonationPaymentProvider>
        </DonationProvider>
      } />

      {/* Taste of Tailtations - Nationwide Pre-Order */}
      <Route path="/taste" element={
        <TasteProvider>
          <TastePaymentProvider>
            <TastePage />
          </TastePaymentProvider>
        </TasteProvider>
      } />

      {/* Tailtations Wholesale B2B - 30-Pack Kit */}
      <Route path="/wholesale" element={
        <WholesaleProvider>
          <TastePaymentProvider>
            <WholesalePage />
          </TastePaymentProvider>
        </WholesaleProvider>
      } />

      {/* Internal: Dry Ice Label Generator (4x6 Class 9 / UN1845) */}
      <Route path="/labels" element={<LabelsPage />} />

      {/* SECRET Routes - Clean URLs */}
      <Route path="/secret" element={<SecretPageWrapper><SecretLandingPage /></SecretPageWrapper>} />
      <Route path="/login" element={<SecretPageWrapper><SecretLoginPage /></SecretPageWrapper>} />
      <Route path="/signup" element={<SecretPageWrapper><SecretRegisterPage /></SecretPageWrapper>} />
      <Route path="/register" element={<SecretPageWrapper><SecretRegisterPage /></SecretPageWrapper>} />
      <Route path="/forgot-password" element={<SecretPageWrapper><SecretForgotPasswordPage /></SecretPageWrapper>} />
      <Route path="/dashboard" element={<SecretPageWrapper><SecretDashboardPage /></SecretPageWrapper>} />
      
      {/* Legacy SECRET Routes */}
      <Route path="/secret/*" element={<SecretApp />} />

      {/* Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/terms-of-sale" element={<TermsOfSale />} />
      <Route path="/terms-of-donation" element={<TermsOfDonation />} />

      {/* Design System - Standalone Documentation - Put at the end */}
      <Route path="/design-system" element={<DesignSystem />} />
    </Routes>
  )
}

export default App